import type {
  CreateLibraryItemCmd,
  DeleteLibraryItemCmd,
  SaveToLibraryCmd,
} from '../commandBuilders.js';
import type {
  LibraryGeometryDef,
  LibraryMaterialDef,
} from '../../scene/layerState.js';
import type { AppState } from '../../state/appState.js';
import type { ActionBase, ActionExecution } from '../commandExecutor.js';
import { cloneLibraryItem, getLibraryStoreFor } from './shared.js';

export interface RestoreLibraryItemAction extends ActionBase<'library:restore'> {
  kind: 'geometry' | 'material';
  item: LibraryGeometryDef | LibraryMaterialDef;
}

export function execCreateLibraryItemFor(
  appState: AppState,
  cmd: CreateLibraryItemCmd,
): ActionExecution<{ id: string }> {
  const store = getLibraryStoreFor(appState, cmd.kind);
  const item = cloneLibraryItem(cmd.item as LibraryGeometryDef & LibraryMaterialDef);
  store[item.id] = item as never;
  return {
    result: { id: item.id },
    undoActions: [{ type: 'library:delete', kind: cmd.kind, id: item.id }],
    changed: true,
  };
}

export function execSaveToLibraryFor(
  appState: AppState,
  cmd: SaveToLibraryCmd,
): ActionExecution<{ id: string }> {
  const store = getLibraryStoreFor(appState, cmd.kind);
  const item = cloneLibraryItem(cmd.item as LibraryGeometryDef & LibraryMaterialDef);
  store[item.id] = item as never;
  return {
    result: { id: item.id },
    undoActions: [{ type: 'library:delete', kind: cmd.kind, id: item.id }],
    changed: true,
  };
}

export function execRestoreLibraryItemFor(
  appState: AppState,
  cmd: RestoreLibraryItemAction,
): ActionExecution<void> {
  const store = getLibraryStoreFor(appState, cmd.kind);
  const item = cloneLibraryItem(cmd.item as LibraryGeometryDef & LibraryMaterialDef);
  store[item.id] = item as never;
  return { result: undefined, undoActions: [], changed: true };
}

export function execDeleteLibraryItemFor(
  appState: AppState,
  cmd: DeleteLibraryItemCmd,
): ActionExecution<void> {
  const store = getLibraryStoreFor(appState, cmd.kind);
  const item = store[cmd.id];
  if (!item) return { result: undefined, undoActions: [], changed: false };
  delete store[cmd.id];
  const clonedItem = cloneLibraryItem(item as LibraryGeometryDef & LibraryMaterialDef);
  return {
    result: undefined,
    undoActions: [{ type: 'library:restore', kind: cmd.kind, item: clonedItem }],
    changed: true,
  };
}
